import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "~/api";
import { SpinnerIcon } from "~/components/icon";
import { useKeybindings } from "~/hooks/useKeyBindings";
import type { Namespace } from "~/types";
import { cn, formatNumber, stripTagPrefix } from "~/utils";

interface NamespaceSearchModalProps {
  onSuccess?: () => void;
  orgId?: string;
  displayedClusterRegions?: string[];
}

const NAMESPACE_SEARCH_LIMIT = 100;

export function NamespaceSearchModal({
  onSuccess,
  orgId,
  displayedClusterRegions,
}: NamespaceSearchModalProps) {
  const navigate = useNavigate();
  const [namespaceSearchQuery, setNamespaceSearchQuery] = useState("");
  const [namespaceSearchLoadedNamespaces, setNamespaceSearchLoadedNamespaces] =
    useState<Namespace[]>([]);
  const [namespaceSearchPrefixResults, setNamespaceSearchPrefixResults] =
    useState<Namespace[]>([]);
  const [namespaceSearchLoading, setNamespaceSearchLoading] = useState(false);
  const [selectedNamespaceIndex, setSelectedNamespaceIndex] = useState(0);
  const namespaceSearchInputRef = useRef<HTMLInputElement>(null);

  const normalizedQuery = useMemo(
    () => stripTagPrefix(namespaceSearchQuery),
    [namespaceSearchQuery],
  );
  const displayedRegions = useMemo(
    () => getUniqueRegions(displayedClusterRegions ?? []),
    [displayedClusterRegions],
  );

  // Org view: load a bounded namespace preview once on open.
  useEffect(() => {
    if (!orgId) return;

    let ignore = false;

    const loadNamespaces = async () => {
      setNamespaceSearchLoading(true);
      try {
        const results = await api.listOrgNamespaces(orgId, displayedRegions);
        if (ignore) return;
        setNamespaceSearchLoadedNamespaces(sortByVectors(results));
        setSelectedNamespaceIndex(0);
      } catch (error) {
        if (ignore) return;
        console.error("Failed to load namespaces:", error);
        setNamespaceSearchLoadedNamespaces([]);
      } finally {
        if (!ignore) setNamespaceSearchLoading(false);
      }
    };

    void loadNamespaces();
    return () => {
      ignore = true;
    };
  }, [orgId, displayedRegions]);

  // Search as the user types. Org search is constrained to the cluster regions
  // already displayed on the org page; global search uses the namespace ID index.
  useEffect(() => {
    if (normalizedQuery.length < 2) {
      setNamespaceSearchPrefixResults([]);
      setNamespaceSearchLoading(false);
      return;
    }

    let ignore = false;
    setNamespaceSearchLoading(true);
    const debounceTimer = setTimeout(() => {
      const fetchNamespaces = async () => {
        try {
          const results = orgId
            ? await api.searchOrgNamespacesByPrefix(
                orgId,
                displayedRegions,
                normalizedQuery,
              )
            : await api.searchNamespacesByPrefix(normalizedQuery);
          if (ignore) return;
          setNamespaceSearchPrefixResults(
            orgId ? sortByVectors(results) : sortByOrgRegion(results),
          );
          setSelectedNamespaceIndex(0);
        } catch (error) {
          if (ignore) return;
          console.error("Failed to search namespaces:", error);
          setNamespaceSearchPrefixResults([]);
        } finally {
          if (!ignore) setNamespaceSearchLoading(false);
        }
      };

      void fetchNamespaces();
    }, 150);

    return () => {
      ignore = true;
      clearTimeout(debounceTimer);
    };
  }, [normalizedQuery, orgId, displayedRegions]);

  const namespaceSearchResults = useMemo(() => {
    if (orgId) {
      if (!normalizedQuery) return namespaceSearchLoadedNamespaces;
      return namespaceSearchPrefixResults;
    }
    return namespaceSearchPrefixResults;
  }, [
    orgId,
    namespaceSearchLoadedNamespaces,
    namespaceSearchPrefixResults,
    normalizedQuery,
  ]);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => {
      namespaceSearchInputRef.current?.focus();
    }, 50);
  }, []);

  const handleSelectNamespace = useCallback(
    (ns: Namespace) => {
      onSuccess?.();
      navigate(
        `/namespaces/${ns.org_id}/${encodeURIComponent(ns.region)}/${encodeURIComponent(ns.id)}`,
      );
    },
    [onSuccess, navigate],
  );

  const emptyStateMessage = useMemo(() => {
    if (!normalizedQuery) {
      return orgId ? "No namespaces found" : "Type to search namespaces";
    }
    if (normalizedQuery.length < 2) {
      return "Type at least 2 characters...";
    }
    return "No namespaces found";
  }, [normalizedQuery, orgId]);

  // Navigation keybindings
  useKeybindings(
    {
      ArrowDown: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedNamespaceIndex((prev) =>
          Math.min(prev + 1, namespaceSearchResults.length - 1),
        );
      },
      ArrowUp: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedNamespaceIndex((prev) => Math.max(prev - 1, 0));
      },
      Enter: (e: KeyboardEvent) => {
        e.preventDefault();
        const selected = namespaceSearchResults[selectedNamespaceIndex];
        if (selected) {
          handleSelectNamespace(selected);
        }
      },
    },
    {
      allowedElements: ["INPUT", "BUTTON", "DIV", "BODY"],
      disallowedElements: [],
      enabled: true,
    },
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <input
          ref={namespaceSearchInputRef}
          type="text"
          value={namespaceSearchQuery}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setNamespaceSearchQuery(nextQuery);
            setSelectedNamespaceIndex(0);
            setNamespaceSearchPrefixResults([]);
            setNamespaceSearchLoading(stripTagPrefix(nextQuery).length >= 2);
          }}
          placeholder="Search by namespace prefix..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          autoComplete="off"
        />
        {namespaceSearchLoading ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <SpinnerIcon className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : null}
      </div>

      <div className="h-64 overflow-y-auto rounded-md border border-gray-200">
        {namespaceSearchLoading ? (
          [...Array(6)].map((_, i) => (
            <div
              key={i}
              className="flex w-full animate-pulse items-center justify-between px-3 py-2"
            >
              <div className="flex flex-col gap-1.5">
                <div className="h-4 w-32 rounded bg-gray-200" />
                <div className="h-3 w-48 rounded bg-gray-100" />
                <div className="h-3 w-40 rounded bg-gray-100" />
              </div>
              <div className="h-5 w-12 rounded-full bg-gray-200" />
            </div>
          ))
        ) : namespaceSearchResults.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {emptyStateMessage}
          </div>
        ) : (
          namespaceSearchResults.map((ns, index) => (
            <button
              key={`${ns.id}:${ns.org_id}:${ns.region}`}
              onClick={() => handleSelectNamespace(ns)}
              onMouseEnter={() => setSelectedNamespaceIndex(index)}
              className={cn(
                "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors",
                index === selectedNamespaceIndex
                  ? "bg-blue-50 text-blue-900"
                  : "hover:bg-gray-50",
              )}
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {ns.id.length > 88 ? `${ns.id.slice(0, 88)}...` : ns.id}
                </span>
                <div>
                  <span className="font-mono text-xs text-gray-500">
                    {ns.org_id}
                  </span>
                  <span className="text-gray-500"> | </span>
                  <span className="font-mono text-xs text-gray-500">
                    {ns.region}
                  </span>
                </div>
              </div>
              <span className="ml-3 shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                {formatNumber(ns.num_vectors)} vecs
              </span>
            </button>
          ))
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        {orgId ? (
          <span>
            {normalizedQuery
              ? `Showing up to ${NAMESPACE_SEARCH_LIMIT} matching namespaces`
              : `Showing first ${NAMESPACE_SEARCH_LIMIT} namespaces`}
          </span>
        ) : (
          <span>
            Showing up to {NAMESPACE_SEARCH_LIMIT} matching namespaces
          </span>
        )}
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Enter</kbd>
          <span>select</span>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Esc</kbd>
          <span>close</span>
        </div>
      </div>
    </div>
  );
}

function getUniqueRegions(regions: string[]) {
  return [...new Set(regions)].sort();
}

function sortByVectors(namespaces: Namespace[]) {
  return [...namespaces].sort((a, b) => {
    const vectors = b.num_vectors - a.num_vectors;
    if (vectors !== 0) return vectors;
    const region = a.region.localeCompare(b.region);
    if (region !== 0) return region;
    return a.id.localeCompare(b.id);
  });
}

// Group global results by org and region, so common namespace names are grouped by org.
// Done client-side to avoid SQL sort cost.
function sortByOrgRegion(namespaces: Namespace[]) {
  return [...namespaces].sort((a, b) => {
    const org = a.org_id.localeCompare(b.org_id);
    if (org !== 0) return org;
    const region = a.region.localeCompare(b.region);
    if (region !== 0) return region;
    return a.id.localeCompare(b.id);
  });
}
